import React, { ReactNode, CSSProperties, forwardRef } from "react";
import clsx from "clsx";

interface AppBoxProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  column?: boolean;
  align?: "start" | "center" | "end" | "stretch";
  justify?: "start" | "center" | "end" | "between" | "around" | "evenly";
  center?: boolean;
  gap?: number;
  isMobile?: boolean;
  fullWidth?: boolean;
  fullHeight?: boolean;
  width?: string | number;
  height?: string | number;
  relative?: boolean;
  absolute?: boolean;
  fixed?: boolean;
  flex?: number;
  inline?: boolean;
}

const AppBox = forwardRef<HTMLDivElement, AppBoxProps>(
  (
    {
      children,
      column = false,
      center = false,
      align,
      justify,
      gap,
      isMobile,
      fullWidth,
      fullHeight,
      width,
      height,
      className = "",
      style = {},
      flex,
      inline = false,
      ...props
    },
    ref
  ) => {
    const dynamicStyle: CSSProperties = {
      ...style,
      width: typeof width === "number" ? `${width}px` : fullWidth ? "100%" : width, // Convert number to px, pass string as-is
      height: typeof height === "number" ? `${height}px` : fullHeight ? "100%" : height, // Convert number to px, pass string as-is
      flex: flex !== undefined ? `${flex} ${flex} 0%` : undefined, // Add dynamic flex styles
    };

    const classes = clsx(
      inline ? "inline-flex" : "flex",
      "transition-all duration-300 ease-in-out",
      {
        "flex-col": column || isMobile,
        "flex-row": !column && !isMobile,
        [`gap-${gap}`]: gap,
        "items-start": align === "start",
        "items-center": align === "center",
        "items-end": align === "end",
        "items-stretch": align === "stretch",
        "justify-start": justify === "start",
        "justify-center": justify === "center",
        "justify-end": justify === "end",
        "justify-between": justify === "between",
        "justify-around": justify === "around",
        "justify-evenly": justify === "evenly",
        "justify-center items-center": center,
      },
      className
    );

    return (
      <div ref={ref} className={classes} style={dynamicStyle} {...props}>
        {children}
      </div>
    );
  }
);

export default AppBox;